import { useState, useEffect, useRef, useCallback, memo } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import calendarIcon from '../../../../assets/calendar.png';
import calendarActiveIcon from '../../../../assets/calendar_active.png';
import './dateFilter.sass';

// Personalizar locale español para mostrar solo primera letra de los días
const esCustom = {
  ...es,
  options: {
    ...es.options,
    weekStartsOn: 0 // 0 = Domingo
  },
  localize: {
    ...es.localize,
    day: (n) => es.localize.day(n).charAt(0).toUpperCase()
  }
};

// Registrar el locale de español personalizado
registerLocale('es', esCustom);

/**
 * Componente de filtrado por fecha
 * Permite seleccionar una fecha específica usando un calendario
 * 
 * @param {Object} props
 * @param {Date} props.selectedDate - Fecha seleccionada
 * @param {Function} props.onDateChange - Callback cuando cambia la fecha
 * @param {Date} props.minDate - Fecha mínima permitida
 * @param {Date} props.maxDate - Fecha máxima permitida
 */
const DateFilter = memo(({ selectedDate, onDateChange, minDate, maxDate, isDisabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const handleDateChange = useCallback((date) => {
    onDateChange(date);
    setIsOpen(false);
  }, [onDateChange]);

  const handleButtonClick = useCallback(() => {
    if (!isDisabled) {
      setIsOpen(prev => !prev);
    }
  }, [isDisabled]);

  // Cerrar el datepicker al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="date-filter-container" ref={containerRef}>
      <button
        className={`date-filter-button ${isDisabled ? 'disabled' : ''}`}
        onClick={handleButtonClick}
        aria-label="Abrir calendario"
        title={isDisabled ? "Desactiva el filtro 'En vivo' para usar el calendario" : "Seleccionar fecha"}
        disabled={isDisabled}
      >
        <img
          src={isOpen ? calendarActiveIcon : calendarIcon}
          alt="Calendario"
          className="date-filter-icon"
        />
      </button>

      {isOpen && (
        <div className="date-filter-picker-wrapper">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            minDate={minDate}
            maxDate={maxDate}
            inline
            dateFormat="dd/MM/yyyy"
            locale="es"
            className="date-filter-picker"
            monthsShown={1}
            weekStart={0}
            todayButton="Hoy"
          />
        </div>
      )}
    </div>
  );
});

DateFilter.displayName = 'DateFilter';

export default DateFilter;